// src/components/SavePromptModal.js - Updated with MyPrompts auto-tag
import React, { useState, useEffect } from 'react';
import { addPrompt, generatePromptName } from '../promptLibraryUtils';
import './SavePromptModal.css';

function SavePromptModal({ isOpen, onClose, promptContent }) {
  const [promptName, setPromptName] = useState('');
  const [isGeneratingName, setIsGeneratingName] = useState(false);
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  // Reset state when modal opens with new prompt
  useEffect(() => {
    if (isOpen && promptContent) {
      // Initialize tags with MyPrompts
      setTags(['MyPrompts']);
      setTagInput('');
      setErrorMessage('');
      
      // Generate a suggested name
      setIsGeneratingName(true);
      
      generatePromptName(promptContent)
        .then(suggestedName => {
          setPromptName(suggestedName);
        })
        .catch(err => {
          console.error('Error generating prompt name:', err);
          // Fallback to simple truncation
          setPromptName(promptContent.length > 30 ? 
            promptContent.substring(0, 30) + "..." : 
            promptContent);
        })
        .finally(() => {
          setIsGeneratingName(false);
        });
    }
  }, [isOpen, promptContent]);
  
  const handleAddTag = () => {
    const trimmedTag = tagInput.trim();
    
    if (!trimmedTag) {
      return;
    }
    
    // Check if tag already exists
    if (tags.includes(trimmedTag)) {
      setErrorMessage('This tag already exists');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }
    
    // Add the new tag
    setTags([...tags, trimmedTag]);
    setTagInput('');
  };
  
  const handleRemoveTag = (tagToRemove) => {
    // Prevent removal of MyPrompts tag
    if (tagToRemove === 'MyPrompts') {
      setErrorMessage("'MyPrompts' tag cannot be removed");
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }
    
    setTags(tags.filter(tag => tag !== tagToRemove));
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };
  
  const handleSave = () => {
    const trimmedName = promptName.trim();
    
    if (!trimmedName) {
      setErrorMessage('Please provide a name for this prompt');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }
    
    if (!promptContent) {
      setErrorMessage('No prompt content to save');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }
    
    // Make sure MyPrompts tag is included
    let finalTags = tags;
    if (!finalTags.includes('MyPrompts')) {
      finalTags = ['MyPrompts', ...tags];
    }
    
    // Save the prompt
    const result = addPrompt(trimmedName, promptContent, finalTags);
    
    if (result) {
      onClose(true); // Pass true to indicate successful save
    } else {
      setErrorMessage('Failed to save prompt');
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="modal-overlay" onClick={(e) => {
      if (e.target.className === 'modal-overlay') onClose();
    }}>
      <div className="modal-container">
        <div className="modal-header">
          <h3>Save to Prompt Library</h3>
          <button className="close-button" onClick={() => onClose()}>×</button>
        </div>
        
        <div className="modal-content">
          {errorMessage && (
            <div className="error-message modal-error">
              {errorMessage}
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="prompt-name">Prompt Name</label>
            {isGeneratingName ? (
              <div className="generating-name">
                <div className="small-spinner"></div>
                <span>Generating name...</span>
              </div>
            ) : (
              <input
                id="prompt-name"
                type="text"
                value={promptName}
                onChange={(e) => setPromptName(e.target.value)}
                placeholder="Enter a name for this prompt"
                autoFocus
              />
            )}
          </div>
          
          <div className="form-group">
            <label>Tags</label>
            <div className="tags-input-container">
              <div className="tags-list">
                {tags.map(tag => (
                  <div key={tag} className={`tag-item ${tag === 'MyPrompts' ? 'system-tag' : ''}`}>
                    <span>{tag}</span>
                    <button 
                      type="button" 
                      className="remove-tag" 
                      onClick={() => handleRemoveTag(tag)}
                      disabled={tag === 'MyPrompts'}
                    >
                      {tag !== 'MyPrompts' ? '×' : ''}
                    </button>
                  </div>
                ))}
              </div>
              <div className="tag-input-wrapper">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Add a tag (press Enter)"
                  className="tag-input"
                />
                <button 
                  type="button"
                  className="add-tag-button"
                  onClick={handleAddTag}
                  disabled={!tagInput.trim()}
                >
                  +
                </button>
              </div>
            </div>
            <p className="helper-text">Tags help you organize and filter your prompts</p>
          </div>
          
          <div className="form-group">
            <label>Prompt Preview</label>
            <div className="prompt-preview">
              {promptContent.length > 200 
                ? promptContent.substring(0, 200) + "..." 
                : promptContent}
            </div>
          </div>
        </div>
        
        <div className="modal-footer">
          <button className="cancel-button" onClick={() => onClose()}>
            Cancel
          </button>
          <button 
            className="save-button"
            onClick={handleSave}
            disabled={!promptName.trim() || isGeneratingName}
          >
            Save to Library
          </button>
        </div>
      </div>
    </div>
  );
}

export default SavePromptModal;